import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useMediaQuery } from '#/shared/lib/hooks'
import {
  TABLE_DENSITY_STYLES,
  useTableCellDividers,
  useTableDensity,
  useTableRowDividers,
  useTableStripedRows,
} from '#/shared/lib/table-density'
import { cn } from '#/shared/lib/utils'
import { nextSort, parseSort } from '#/shared/lib/list-sort'
import { Checkbox } from '#/shared/ui/checkbox'
import { Skeleton } from '#/shared/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '#/shared/ui/table'
import { TablePagination } from '#/shared/ui/table-pagination'

import { ColumnPinPopover } from './column-pin-popover'
import { SortableHeader } from './sortable-header'
import { TableSettingsMenu } from './table-settings-menu'
import type {
  DataTableColumn,
  DataTableEmptyVariant,
  DataTablePagination,
  DataTablePin,
  DataTableSection,
} from './data-table-types'
import { useColumnPins } from './use-column-pins'

const DEFAULT_SKELETON_ROWS = 10
const SELECT_COLUMN_KEY = '__select__'

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  /**
   * The order the server sent, in its own wire form (`field` / `-field`).
   * Supply both this and `onSortChange` to make columns carrying a `sortKey`
   * clickable; without them the headers stay plain text.
   */
  sort?: string | null
  onSortChange?: (next: string) => void
  /**
   * Render the body as labelled groups instead of a flat list. When provided it
   * supersedes `data` for the body; selection, ids, and striping run over the
   * sections' rows in order. Skeleton/empty states still apply.
   */
  sections?: DataTableSection<T>[]
  selectable?: boolean
  getRowId?: (row: T) => string
  onRowClick?: (row: T) => void
  onSelectionChange?: (selectedIds: string[]) => void
  /**
   * Controlled selection: when provided, the table renders exactly these ids
   * as checked and reports every change through `onSelectionChange` without
   * keeping its own copy — so the owner can reset (pass `[]`) or restore a
   * saved selection (workspace-tab switches). Omit for local, uncontrolled
   * selection.
   */
  selectedIds?: string[]
  className?: string
  emptyVariant?: DataTableEmptyVariant
  emptyState?: React.ReactNode
  pagination?: DataTablePagination
  isLoading?: boolean
  minWidth?: number | string
  storageKey?: string
  rowHeight?: string
  /**
   * Vertical alignment of cell content. Defaults to `'top'` so multi-line cells
   * keep their primary line aligned across columns. Single-line tables (or any
   * table given a custom `rowHeight` taller than its content) should pass
   * `'middle'` to vertically center the content instead of stranding it at top.
   */
  cellAlign?: 'top' | 'middle'
  /**
   * When provided, the table collapses to a vertical list of these cards below
   * the `md` breakpoint (the desktop table renders at/above it). Lets the
   * leads/quotes/orders lists swap their squeezed table for mobile cards.
   */
  renderCard?: (row: T) => React.ReactNode
  /**
   * Fill the available height instead of growing with rows: the table caps at
   * its flex parent's remaining space, the header sticks, and the body scrolls
   * vertically. Desktop-only (mobile keeps the page scroll). Requires every
   * ancestor up to the layout to pass height down (page root `md:flex-1
   * md:min-h-0` inside the layout's `min-h-full` column).
   */
  fillHeight?: boolean
}

function DefaultEmptyState({ variant }: { variant: DataTableEmptyVariant }) {
  const { t } = useTranslation()
  const titleKey =
    variant === 'filtered'
      ? 'table.empty.filteredTitle'
      : 'table.empty.initialTitle'
  const descKey =
    variant === 'filtered'
      ? 'table.empty.filteredDescription'
      : 'table.empty.initialDescription'
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-12 text-center">
      <p className="text-sm font-semibold text-gray-900">{t(titleKey)}</p>
      <p className="text-sm text-gray-500">{t(descKey)}</p>
    </div>
  )
}

function defaultGetRowId<T>(row: T, index: number): string {
  if (row && typeof row === 'object' && 'id' in row) {
    const id = (row as { id: unknown }).id
    if (typeof id === 'string' || typeof id === 'number') return String(id)
  }
  return String(index)
}

function resolveRowId<T>(
  row: T,
  index: number,
  getRowId?: (row: T) => string,
): string {
  return getRowId ? getRowId(row) : defaultGetRowId(row, index)
}

interface TableView {
  showSkeleton: boolean
  hideHeader: boolean
  hasPaginationBar: boolean
  skeletonRowCount: number
}

function deriveTableView({
  dataLength,
  isLoading,
  emptyVariant,
  pagination,
}: {
  dataLength: number
  isLoading?: boolean
  emptyVariant: DataTableEmptyVariant
  pagination?: DataTablePagination
}): TableView {
  const isEmpty = dataLength === 0
  const showSkeleton = isLoading === true && isEmpty
  const hideHeader = !showSkeleton && isEmpty && emptyVariant === 'initial'
  const totalItems = pagination?.totalItems ?? 0
  const hasPaginationBar = totalItems > 0
  const skeletonRowCount = pagination?.pageSize ?? DEFAULT_SKELETON_ROWS
  return { showSkeleton, hideHeader, hasPaginationBar, skeletonRowCount }
}

function renderCell<T>(col: DataTableColumn<T>, row: T): React.ReactNode {
  if (col.render) return col.render(row)
  return String(row[col.accessor] ?? '')
}

type PinMap = Map<string, DataTablePin>
type PinStyleMap = Map<string, React.CSSProperties>
type ShadowSide = 'right' | 'left'
type ShadowSideMap = Map<string, ShadowSide>
type HeaderRefSetter = (
  key: string,
) => (el: HTMLTableCellElement | null) => void

interface PinComputation {
  styles: PinStyleMap
  shadowSides: ShadowSideMap
}

function applyLeftPins<T>({
  columns,
  selectable,
  widths,
  effectivePins,
  styles,
}: {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  widths: Record<string, number>
  effectivePins: PinMap
  styles: PinStyleMap
}): string | undefined {
  const hasLeftPin = columns.some((c) => effectivePins.get(c.key) === 'left')
  let offset = 0
  let lastKey: string | undefined
  if (selectable && hasLeftPin) {
    styles.set(SELECT_COLUMN_KEY, { position: 'sticky', left: 0, zIndex: 1 })
    lastKey = SELECT_COLUMN_KEY
    offset = widths[SELECT_COLUMN_KEY] ?? 0
  }
  for (const col of columns) {
    if (effectivePins.get(col.key) !== 'left') continue
    styles.set(col.key, { position: 'sticky', left: offset, zIndex: 1 })
    lastKey = col.key
    offset += widths[col.key] ?? 0
  }
  return lastKey
}

function applyRightPins<T>({
  columns,
  widths,
  effectivePins,
  styles,
}: {
  columns: DataTableColumn<T>[]
  widths: Record<string, number>
  effectivePins: PinMap
  styles: PinStyleMap
}): string | undefined {
  let offset = 0
  let firstKey: string | undefined
  for (let i = columns.length - 1; i >= 0; i--) {
    const col = columns[i]
    if (effectivePins.get(col.key) !== 'right') continue
    styles.set(col.key, { position: 'sticky', right: offset, zIndex: 1 })
    firstKey = col.key
    offset += widths[col.key] ?? 0
  }
  return firstKey
}

function buildPinStyles<T>({
  columns,
  selectable,
  widths,
  effectivePins,
  canScrollLeft,
  canScrollRight,
}: {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  widths: Record<string, number>
  effectivePins: PinMap
  canScrollLeft: boolean
  canScrollRight: boolean
}): PinComputation {
  const styles: PinStyleMap = new Map()
  const shadowSides: ShadowSideMap = new Map()
  const lastLeftKey = applyLeftPins({
    columns,
    selectable,
    widths,
    effectivePins,
    styles,
  })
  const firstRightKey = applyRightPins({
    columns,
    widths,
    effectivePins,
    styles,
  })
  if (lastLeftKey && canScrollLeft) shadowSides.set(lastLeftKey, 'right')
  if (firstRightKey && canScrollRight) shadowSides.set(firstRightKey, 'left')
  return { styles, shadowSides }
}

const SHADOW_WIDTH = 8
const SHADOW_GRADIENT_RIGHT =
  'linear-gradient(to right, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0))'
const SHADOW_GRADIENT_LEFT =
  'linear-gradient(to left, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0))'

function PinShadowOverlay({ side }: { side: ShadowSide }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-0 bottom-0 z-10"
      style={{
        width: SHADOW_WIDTH,
        background:
          side === 'right' ? SHADOW_GRADIENT_RIGHT : SHADOW_GRADIENT_LEFT,
        ...(side === 'right'
          ? { right: -SHADOW_WIDTH }
          : { left: -SHADOW_WIDTH }),
      }}
    />
  )
}

function PinShadow({
  cellKey,
  shadowSides,
}: {
  cellKey: string
  shadowSides: ShadowSideMap
}) {
  const side = shadowSides.get(cellKey)
  if (!side) return null
  return <PinShadowOverlay side={side} />
}

function widthsEqual(
  a: Record<string, number>,
  b: Record<string, number>,
): boolean {
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) return false
  return keys.every((k) => a[k] === b[k])
}

function useStickyColumns<T>({
  columns,
  selectable,
  effectivePins,
  canScrollLeft,
  canScrollRight,
}: {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  effectivePins: PinMap
  canScrollLeft: boolean
  canScrollRight: boolean
}) {
  const headerRefs = useRef(new Map<string, HTMLTableCellElement>())
  const [widths, setWidths] = useState<Record<string, number>>({})

  const registerHeaderRef: HeaderRefSetter = useCallback(
    (key) => (el) => {
      if (el) headerRefs.current.set(key, el)
      else headerRefs.current.delete(key)
    },
    [],
  )

  const columnKey = columns.map((c) => c.key).join('|')
  useLayoutEffect(() => {
    const measure = () => {
      const next: Record<string, number> = {}
      headerRefs.current.forEach((el, key) => {
        next[key] = el.getBoundingClientRect().width
      })
      setWidths((prev) => (widthsEqual(prev, next) ? prev : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    headerRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [columnKey, selectable])

  const { styles: pinStyles, shadowSides } = useMemo(
    () =>
      buildPinStyles({
        columns,
        selectable,
        widths,
        effectivePins,
        canScrollLeft,
        canScrollRight,
      }),
    [columns, selectable, widths, effectivePins, canScrollLeft, canScrollRight],
  )

  return { registerHeaderRef, pinStyles, shadowSides }
}

function useTableLayout<T>({
  columns,
  selectable,
  storageKey,
}: {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  storageKey?: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollEdges = useScrollEdges(scrollRef)
  const { effectivePins, setPin } = useColumnPins({ columns, storageKey })
  const { registerHeaderRef, pinStyles, shadowSides } = useStickyColumns({
    columns,
    selectable,
    effectivePins,
    canScrollLeft: scrollEdges.left,
    canScrollRight: scrollEdges.right,
  })
  return {
    scrollRef,
    effectivePins,
    setPin,
    registerHeaderRef,
    pinStyles,
    shadowSides,
  }
}

function useScrollEdges(ref: React.RefObject<HTMLDivElement | null>) {
  const [edges, setEdges] = useState({ left: false, right: false })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth
      const left = el.scrollLeft > 0
      const right = el.scrollLeft < maxScroll - 1
      setEdges((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right },
      )
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [ref])
  return edges
}

function useTableSelection(
  allIds: string[],
  onSelectionChange?: (ids: string[]) => void,
  controlledIds?: string[],
) {
  // Uncontrolled fallback for tables whose owner doesn't track the selection.
  const [localIds, setLocalIds] = useState<Set<string>>(new Set())
  const selectedIds = useMemo(
    () => (controlledIds ? new Set(controlledIds) : localIds),
    [controlledIds, localIds],
  )

  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.has(id))
  const someSelected = allIds.some((id) => selectedIds.has(id))
  const headerCheckedState: boolean | 'indeterminate' = allSelected
    ? true
    : someSelected
      ? 'indeterminate'
      : false

  const emit = (next: Set<string>) => {
    if (!controlledIds) setLocalIds(next)
    onSelectionChange?.(Array.from(next))
  }

  const toggleAll = () => {
    emit(allSelected ? new Set<string>() : new Set(allIds))
  }

  const toggleRow = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    emit(next)
  }

  return { selectedIds, headerCheckedState, toggleAll, toggleRow }
}

function SelectCellContent({
  checked,
  onCheckedChange,
}: {
  checked: boolean | 'indeterminate'
  onCheckedChange: () => void
}) {
  return (
    <div className="flex h-7 items-center justify-start">
      <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

const SELECT_CELL_CLASS = 'w-10 pl-2.5'

function pinnedClass(style: React.CSSProperties | undefined): string | false {
  return style ? 'bg-inherit' : false
}

function PaginationFooter({
  pagination,
  bare,
}: {
  pagination?: DataTablePagination
  bare?: boolean
}) {
  if (!pagination) return null
  // Console addition: the product hides this bar when the whole dataset fits
  // one page; the console keeps it visible — the count and the per-page
  // selector orient the operator on every screen, full page or not.
  return (
    <TablePagination
      page={pagination.page}
      pageSize={pagination.pageSize}
      totalItems={pagination.totalItems}
      totalPages={pagination.totalPages}
      onPageChange={pagination.onPageChange}
      onPageSizeChange={pagination.onPageSizeChange}
      window={pagination.window}
      bare={bare}
    />
  )
}

// Header column dividers. When the global toggle is on they're always visible;
// otherwise they only appear while hovering the header row (group/divider).
// Colors are right-side-scoped: an all-side `border-transparent` would also
// blank the sticky header's bottom border (fill mode).
function headerDividerClass(showDividers: boolean): string {
  return showDividers
    ? 'border-r border-r-gray-200'
    : 'border-r border-r-transparent transition-colors group-hover/divider:border-r-gray-200'
}

interface HeaderCellProps<T> {
  col: DataTableColumn<T>
  pinStyle: React.CSSProperties | undefined
  currentPin: DataTablePin | undefined
  onPinChange: (key: string, pin: DataTablePin | null) => void
  refSetter: (el: HTMLTableCellElement | null) => void
  shadowSides: ShadowSideMap
  showDividers: boolean
  sort?: string | null
  onSortChange?: (next: string) => void
}

function HeaderCell<T>({
  col,
  pinStyle,
  currentPin,
  onPinChange,
  refSetter,
  shadowSides,
  showDividers,
  sort,
  onSortChange,
}: HeaderCellProps<T>) {
  const sorted = col.sortKey ? parseSort(sort) : null
  const ariaSort =
    sorted && sorted.field === col.sortKey
      ? sorted.dir === 'asc'
        ? 'ascending'
        : 'descending'
      : col.sortKey
        ? 'none'
        : undefined

  return (
    <TableHead
      ref={refSetter}
      style={pinStyle}
      {...(ariaSort ? { 'aria-sort': ariaSort } : {})}
      className={cn(
        'relative whitespace-nowrap last:border-r-0',
        headerDividerClass(showDividers),
        col.className,
        pinnedClass(pinStyle),
      )}
    >
      <div className="group flex items-center justify-between gap-1.5">
        {col.sortKey && onSortChange ? (
          <SortableHeader
            label={col.header}
            field={col.sortKey}
            sort={sort}
            onToggle={() => onSortChange(nextSort(sort, col.sortKey as string, col.sortDefault))}
          />
        ) : (
          <span className="select-none">{col.header}</span>
        )}
        <ColumnPinPopover
          currentPin={currentPin}
          onChange={(pin) => onPinChange(col.key, pin)}
        />
      </div>
      <PinShadow cellKey={col.key} shadowSides={shadowSides} />
    </TableHead>
  )
}

interface HeaderRowProps<T> {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  headerCheckedState: boolean | 'indeterminate'
  onToggleAll: () => void
  pinStyles: PinStyleMap
  shadowSides: ShadowSideMap
  effectivePins: PinMap
  onPinChange: (key: string, pin: DataTablePin | null) => void
  registerHeaderRef: HeaderRefSetter
  showDividers: boolean
  sticky?: boolean
  sort?: string | null
  onSortChange?: (next: string) => void
}

function HeaderRow<T>({
  columns,
  selectable,
  headerCheckedState,
  onToggleAll,
  pinStyles,
  shadowSides,
  effectivePins,
  onPinChange,
  registerHeaderRef,
  showDividers,
  sticky,
  sort,
  onSortChange,
}: HeaderRowProps<T>) {
  const selectStyle = pinStyles.get(SELECT_COLUMN_KEY)
  return (
    // z-10 keeps the stuck header above the pinned body cells (z-1) but under
    // the settings-gear overlay (z-20). The bottom border moves onto the cells
    // because a tr border neither sticks nor paints under border-separate.
    <TableHeader className={cn(sticky && 'sticky top-0 z-10 [&_th]:border-b')}>
      <TableRow className="group/divider bg-white">
        {selectable && (
          <TableHead
            ref={registerHeaderRef(SELECT_COLUMN_KEY)}
            style={selectStyle}
            className={cn(
              'relative',
              headerDividerClass(showDividers),
              SELECT_CELL_CLASS,
              pinnedClass(selectStyle),
            )}
          >
            <SelectCellContent
              checked={headerCheckedState}
              onCheckedChange={onToggleAll}
            />
            <PinShadow cellKey={SELECT_COLUMN_KEY} shadowSides={shadowSides} />
          </TableHead>
        )}
        {columns.map((col) => (
          <HeaderCell
            key={col.key}
            col={col}
            pinStyle={pinStyles.get(col.key)}
            currentPin={effectivePins.get(col.key)}
            onPinChange={onPinChange}
            refSetter={registerHeaderRef(col.key)}
            shadowSides={shadowSides}
            showDividers={showDividers}
            sort={sort}
            {...(onSortChange ? { onSortChange } : {})}
          />
        ))}
      </TableRow>
    </TableHeader>
  )
}

interface DataRowProps<T> {
  row: T
  rowId: string
  rowIndex: number
  columns: DataTableColumn<T>[]
  selectable?: boolean
  isSelected: boolean
  onToggle: () => void
  onRowClick?: () => void
  pinStyles: PinStyleMap
  shadowSides: ShadowSideMap
  rowHeight: string
  cellClassName?: string
  rowDividers: boolean
  stripedRows: boolean
}

function DataRow<T>({
  row,
  rowId,
  rowIndex,
  columns,
  selectable,
  isSelected,
  onToggle,
  onRowClick,
  pinStyles,
  shadowSides,
  rowHeight,
  cellClassName,
  rowDividers,
  stripedRows,
}: DataRowProps<T>) {
  const selectStyle = pinStyles.get(SELECT_COLUMN_KEY)
  return (
    <TableRow
      key={rowId}
      onClick={onRowClick}
      className={cn(
        'animate-fade-in',
        rowHeight,
        !rowDividers && 'border-b-0',
        stripedRows && rowIndex % 2 === 1 && 'bg-surface-muted',
        isSelected && 'bg-blue-50',
        onRowClick && 'cursor-pointer',
      )}
    >
      {selectable && (
        <TableCell
          style={selectStyle}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'relative',
            SELECT_CELL_CLASS,
            cellClassName,
            pinnedClass(selectStyle),
          )}
        >
          <SelectCellContent checked={isSelected} onCheckedChange={onToggle} />
          <PinShadow cellKey={SELECT_COLUMN_KEY} shadowSides={shadowSides} />
        </TableCell>
      )}
      {columns.map((col) => {
        const style = pinStyles.get(col.key)
        return (
          <TableCell
            key={col.key}
            style={style}
            className={cn(
              'relative whitespace-nowrap',
              col.className,
              cellClassName,
              pinnedClass(style),
            )}
          >
            {renderCell(col, row)}
            <PinShadow cellKey={col.key} shadowSides={shadowSides} />
          </TableCell>
        )
      })}
    </TableRow>
  )
}

function EmptyRow<T>({
  columns,
  selectable,
  emptyVariant,
  emptyState,
}: {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  emptyVariant: DataTableEmptyVariant
  emptyState?: React.ReactNode
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={columns.length + (selectable ? 1 : 0)}
        className="p-0"
      >
        {emptyState ?? <DefaultEmptyState variant={emptyVariant} />}
      </TableCell>
    </TableRow>
  )
}

const SKELETON_PRIMARY_WIDTHS = ['w-3/4', 'w-2/3', 'w-4/5', 'w-1/2', 'w-5/6']
const SKELETON_SECONDARY_WIDTHS = ['w-1/3', 'w-2/5', 'w-1/4', 'w-2/5', 'w-1/3']

function SkeletonCellContent({ columnIndex }: { columnIndex: number }) {
  const primary =
    SKELETON_PRIMARY_WIDTHS[columnIndex % SKELETON_PRIMARY_WIDTHS.length]
  const secondary =
    SKELETON_SECONDARY_WIDTHS[columnIndex % SKELETON_SECONDARY_WIDTHS.length]
  return (
    <div className="flex flex-col gap-1.5">
      <Skeleton className={cn('h-3', primary)} />
      <Skeleton className={cn('h-3 opacity-50', secondary)} />
    </div>
  )
}

function SkeletonRows<T>({
  columns,
  selectable,
  rowCount,
  pinStyles,
  shadowSides,
  rowHeight,
  cellClassName,
}: {
  columns: DataTableColumn<T>[]
  selectable?: boolean
  rowCount: number
  pinStyles: PinStyleMap
  shadowSides: ShadowSideMap
  rowHeight: string
  cellClassName?: string
}) {
  const selectStyle = pinStyles.get(SELECT_COLUMN_KEY)
  return (
    <>
      {Array.from({ length: rowCount }).map((_, i) => (
        <TableRow
          key={`skeleton-${i}`}
          className={cn(rowHeight, 'hover:bg-transparent')}
        >
          {selectable && (
            <TableCell
              style={selectStyle}
              className={cn(
                'relative',
                SELECT_CELL_CLASS,
                cellClassName,
                pinnedClass(selectStyle),
              )}
            >
              <div className="flex h-7 items-center justify-start">
                <Skeleton className="size-5 rounded-none" />
              </div>
              <PinShadow
                cellKey={SELECT_COLUMN_KEY}
                shadowSides={shadowSides}
              />
            </TableCell>
          )}
          {columns.map((col, colIndex) => {
            const style = pinStyles.get(col.key)
            return (
              <TableCell
                key={col.key}
                style={style}
                className={cn(
                  'relative whitespace-nowrap',
                  col.className,
                  cellClassName,
                  pinnedClass(style),
                )}
              >
                <SkeletonCellContent columnIndex={colIndex} />
                <PinShadow cellKey={col.key} shadowSides={shadowSides} />
              </TableCell>
            )
          })}
        </TableRow>
      ))}
    </>
  )
}

function SectionLabelRow({
  colSpan,
  label,
}: {
  colSpan: number
  label: string
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={colSpan}
        // border-b on the cell (not just the tr) so the divider under the
        // label also paints in fill mode's border-separate table.
        className="border-b bg-surface-muted py-2 pl-3 text-2xs font-medium tracking-wide text-gray-400 uppercase"
      >
        {label}
      </TableCell>
    </TableRow>
  )
}

function SectionFooterRow({
  colSpan,
  children,
}: {
  colSpan: number
  children: React.ReactNode
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="p-0">
        {children}
      </TableCell>
    </TableRow>
  )
}

interface SectionedRowsProps<T> {
  sections: DataTableSection<T>[]
  columns: DataTableColumn<T>[]
  allIds: string[]
  selectable?: boolean
  selectedIds: Set<string>
  onToggleRow: (id: string) => void
  onRowClick?: (row: T) => void
  pinStyles: PinStyleMap
  shadowSides: ShadowSideMap
  rowHeight: string
  cellClassName?: string
  rowDividers: boolean
  stripedRows: boolean
}

function SectionedRows<T>({
  sections,
  columns,
  allIds,
  selectable,
  selectedIds,
  onToggleRow,
  onRowClick,
  pinStyles,
  shadowSides,
  rowHeight,
  cellClassName,
  rowDividers,
  stripedRows,
}: SectionedRowsProps<T>) {
  const colSpan = columns.length + (selectable ? 1 : 0)
  const nodes: React.ReactNode[] = []
  let index = 0
  for (const section of sections) {
    if (section.label) {
      nodes.push(
        <SectionLabelRow
          key={`label-${section.key}`}
          colSpan={colSpan}
          label={section.label}
        />,
      )
    }
    for (const row of section.rows) {
      const rowId = allIds[index] ?? String(index)
      nodes.push(
        <DataRow
          key={rowId}
          row={row}
          rowId={rowId}
          rowIndex={index}
          columns={columns}
          selectable={selectable}
          isSelected={selectedIds.has(rowId)}
          onToggle={() => onToggleRow(rowId)}
          onRowClick={onRowClick ? () => onRowClick(row) : undefined}
          pinStyles={pinStyles}
          shadowSides={shadowSides}
          rowHeight={rowHeight}
          cellClassName={cellClassName}
          rowDividers={rowDividers}
          stripedRows={stripedRows}
        />,
      )
      index += 1
    }
    if (section.footer) {
      nodes.push(
        <SectionFooterRow key={`footer-${section.key}`} colSpan={colSpan}>
          {section.footer}
        </SectionFooterRow>,
      )
    }
  }
  return <>{nodes}</>
}

interface TableBodyContentProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  sections?: DataTableSection<T>[]
  allIds: string[]
  selectable?: boolean
  selectedIds: Set<string>
  onToggleRow: (id: string) => void
  onRowClick?: (row: T) => void
  showSkeleton: boolean
  skeletonRowCount: number
  emptyVariant: DataTableEmptyVariant
  emptyState?: React.ReactNode
  pinStyles: PinStyleMap
  shadowSides: ShadowSideMap
  rowHeight: string
  cellClassName?: string
  rowDividers: boolean
  stripedRows: boolean
}

function TableBodyContent<T>({
  columns,
  data,
  sections,
  allIds,
  selectable,
  selectedIds,
  onToggleRow,
  onRowClick,
  showSkeleton,
  skeletonRowCount,
  emptyVariant,
  emptyState,
  pinStyles,
  shadowSides,
  rowHeight,
  cellClassName,
  rowDividers,
  stripedRows,
}: TableBodyContentProps<T>) {
  if (showSkeleton) {
    return (
      <SkeletonRows
        columns={columns}
        selectable={selectable}
        rowCount={skeletonRowCount}
        pinStyles={pinStyles}
        shadowSides={shadowSides}
        rowHeight={rowHeight}
        cellClassName={cellClassName}
      />
    )
  }
  if (data.length === 0) {
    return (
      <EmptyRow
        columns={columns}
        selectable={selectable}
        emptyVariant={emptyVariant}
        emptyState={emptyState}
      />
    )
  }
  // A flat list is the degenerate single section: all rows, no band label.
  const flatSections: DataTableSection<T>[] = sections ?? [
    { key: '__all__', rows: data },
  ]
  return (
    <SectionedRows
      sections={flatSections}
      columns={columns}
      allIds={allIds}
      selectable={selectable}
      selectedIds={selectedIds}
      onToggleRow={onToggleRow}
      onRowClick={onRowClick}
      pinStyles={pinStyles}
      shadowSides={shadowSides}
      rowHeight={rowHeight}
      cellClassName={cellClassName}
      rowDividers={rowDividers}
      stripedRows={stripedRows}
    />
  )
}

function MobileCardSkeletons({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-2.5">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={`card-skeleton-${i}`}
          className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4"
        >
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-4 w-1/4" />
        </div>
      ))}
    </div>
  )
}

interface MobileCardListProps<T> {
  data: T[]
  allIds: string[]
  renderCard: (row: T) => React.ReactNode
  onRowClick?: (row: T) => void
  view: TableView
  emptyVariant: DataTableEmptyVariant
  emptyState?: React.ReactNode
  pagination?: DataTablePagination
  className?: string
}

function MobileCardList<T>({
  data,
  allIds,
  renderCard,
  onRowClick,
  view,
  emptyVariant,
  emptyState,
  pagination,
  className,
}: MobileCardListProps<T>) {
  return (
    <div className={cn('min-w-0', className)}>
      {view.showSkeleton ? (
        <MobileCardSkeletons count={view.skeletonRowCount} />
      ) : data.length === 0 ? (
        (emptyState ?? <DefaultEmptyState variant={emptyVariant} />)
      ) : (
        <div className="flex flex-col gap-2.5">
          {data.map((row, i) => (
            <div
              key={allIds[i] ?? String(i)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn('animate-fade-in', onRowClick && 'cursor-pointer')}
            >
              {renderCard(row)}
            </div>
          ))}
        </div>
      )}
      <PaginationFooter pagination={pagination} bare />
      <div className="pb-24" />
    </div>
  )
}

function useDataTableState<T>({
  columns,
  data,
  selectable,
  getRowId,
  onSelectionChange,
  selectedIds,
  emptyVariant,
  pagination,
  isLoading,
  storageKey,
  rowHeight: rowHeightProp,
  cellAlign,
  fillHeight,
}: DataTableProps<T> & { emptyVariant: DataTableEmptyVariant }) {
  const allIds = data.map((row, i) => resolveRowId(row, i, getRowId))
  const selection = useTableSelection(allIds, onSelectionChange, selectedIds)
  const view = deriveTableView({
    dataLength: data.length,
    isLoading,
    emptyVariant,
    pagination,
  })
  const layout = useTableLayout({ columns, selectable, storageKey })
  // Global, persisted row density; an explicit `rowHeight` prop still wins.
  const densityStyle = TABLE_DENSITY_STYLES[useTableDensity()]
  const showDividers = useTableCellDividers()
  const rowDividers = useTableRowDividers()
  const stripedRows = useTableStripedRows()
  const rowHeight = rowHeightProp ?? densityStyle.rowHeight
  const cellClassName = cn(
    densityStyle.cellPaddingY,
    cellAlign === 'middle' && 'align-middle',
    showDividers && 'border-r border-gray-200 last:border-r-0',
    // Fill mode switches the table to border-separate (so the sticky header
    // keeps its border), where tr borders don't paint — row dividers move
    // onto the cells.
    fillHeight && rowDividers && 'md:border-b',
  )
  return {
    allIds,
    selection,
    view,
    layout,
    rowHeight,
    cellClassName,
    showDividers,
    rowDividers,
    stripedRows,
  }
}

/**
 * Extra classes for `fillHeight` mode, all `md:`-scoped so mobile keeps the
 * page scroll: the table becomes a flex column capped at its parent's height
 * and the body scrolls inside. `border-separate` because border-collapse pins
 * collapsed borders to the table grid, so they wouldn't travel with the sticky
 * header; the row dividers then move onto the cells (see `cellClassName`), and
 * the last row drops its divider so it doesn't double the frame border. The
 * frame gets no flex-1: a short table hugs its rows; a long one shrinks
 * (min-h-0) to the remaining space.
 */
function fillModeClasses(fillHeight?: boolean) {
  if (!fillHeight) return {}
  return {
    root: 'md:flex md:min-h-0 md:flex-1 md:flex-col',
    frame: 'md:flex md:min-h-0 md:flex-col',
    scroll: 'md:min-h-0 md:overflow-y-auto',
    table: 'md:border-separate md:border-spacing-0',
    body: 'md:[&_tr:last-child_td]:border-b-0',
  }
}

/**
 * Gear menu overlaid on the header's right edge. The wrapper is click-through;
 * only the button itself isn't, and the white→transparent fade keeps it
 * readable over header text — so it never shifts a header column's position.
 */
function HeaderSettingsOverlay() {
  return (
    <div className="pointer-events-none absolute top-px right-px z-20 flex h-9.5 items-center rounded-tr-md bg-gradient-to-l from-white via-white to-transparent pr-1.5 pl-6">
      <span className="pointer-events-auto">
        <TableSettingsMenu />
      </span>
    </div>
  )
}

/**
 * Jumping to another page (or window range) should land at the top of the body
 * — otherwise the scroller keeps the previous page's offset and the new rows
 * open mid-list. Only the body scrolls (fillHeight mode); in the page-scroll
 * layout the container has no vertical offset, so this is a no-op there.
 */
function useResetBodyScrollOnPageChange(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  pagination?: DataTablePagination,
) {
  const signal = `${pagination?.page ?? ''}:${pagination?.window?.from ?? ''}`
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = 0
  }, [scrollRef, signal])
}

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    data,
    sort,
    onSortChange,
    sections,
    selectable,
    onRowClick,
    className,
    emptyVariant = 'initial',
    emptyState,
    pagination,
    minWidth,
    renderCard,
    fillHeight,
  } = props
  // In grouped mode the sections are the source of truth; flatten them (in
  // section order) so selection, ids, and striping line up with what renders.
  const flatData = useMemo(
    () => (sections ? sections.flatMap((section) => section.rows) : data),
    [sections, data],
  )
  const {
    allIds,
    selection,
    view,
    layout,
    rowHeight,
    cellClassName,
    showDividers,
    rowDividers,
    stripedRows,
  } = useDataTableState({
    ...props,
    data: flatData,
    emptyVariant,
  })
  // Phones get a card list; the table needs ~900px and squeezes badly below it.
  const isDesktop = useMediaQuery('(min-width: 768px)')
  useResetBodyScrollOnPageChange(layout.scrollRef, pagination)

  const tableMinWidth =
    typeof minWidth === 'number' ? `${minWidth}px` : minWidth
  const fill = fillModeClasses(fillHeight)

  if (renderCard && !isDesktop) {
    return (
      <MobileCardList
        data={flatData}
        allIds={allIds}
        renderCard={renderCard}
        onRowClick={onRowClick}
        view={view}
        emptyVariant={emptyVariant}
        emptyState={emptyState}
        pagination={pagination}
        className={className}
      />
    )
  }

  return (
    <div
      // The dashboard layout caps its page column at the viewport height only
      // when a fill-height table is present (`has-[[data-fill-height]]`).
      data-fill-height={fillHeight ? '' : undefined}
      className={cn('relative min-w-0', fill.root, className)}
    >
      {!view.hideHeader && <HeaderSettingsOverlay />}
      <div
        className={cn(
          // Border lives on this non-scrolling wrapper with `overflow-hidden`
          // so it clips the sticky/pinned cells (z-indexed, opaque background)
          // to inside the frame — otherwise they paint over the top/bottom
          // border and the row dividers across the pinned column.
          'w-full overflow-hidden border border-gray-200 bg-white',
          view.hasPaginationBar ? 'rounded-t-md' : 'rounded-md',
          fill.frame,
        )}
      >
        <div
          ref={layout.scrollRef}
          className={cn('w-full overflow-x-auto', fill.scroll)}
        >
          <table
            className={cn('w-full caption-bottom text-sm', fill.table)}
            style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}
          >
            {!view.hideHeader && (
              <HeaderRow
                columns={columns}
                selectable={selectable}
                headerCheckedState={selection.headerCheckedState}
                onToggleAll={selection.toggleAll}
                pinStyles={layout.pinStyles}
                shadowSides={layout.shadowSides}
                effectivePins={layout.effectivePins}
                onPinChange={layout.setPin}
                registerHeaderRef={layout.registerHeaderRef}
                showDividers={showDividers}
                sticky={fillHeight}
                sort={sort}
                {...(onSortChange ? { onSortChange } : {})}
              />
            )}
            <TableBody className={fill.body}>
              <TableBodyContent
                columns={columns}
                data={flatData}
                sections={sections}
                allIds={allIds}
                selectable={selectable}
                selectedIds={selection.selectedIds}
                onToggleRow={selection.toggleRow}
                onRowClick={onRowClick}
                showSkeleton={view.showSkeleton}
                skeletonRowCount={view.skeletonRowCount}
                emptyVariant={emptyVariant}
                emptyState={emptyState}
                pinStyles={layout.pinStyles}
                shadowSides={layout.shadowSides}
                rowHeight={rowHeight}
                cellClassName={cellClassName}
                rowDividers={rowDividers}
                stripedRows={stripedRows}
              />
            </TableBody>
          </table>
        </div>
      </div>
      <PaginationFooter pagination={pagination} />
    </div>
  )
}
